import type { KitchenSLASettings } from '../types';

/** Typography classes for Kitchen Display; scales item names and special instructions together. */
export type KdsTypography = {
  itemRow: string;
  noteBody: string;
  noteLabel: string;
  doneBtn: string;
  startBtn: string;
  removeBtn: string;
  cardTitle: string;
  runningTitle: string;
  runningSub: string;
  batchTimeLine: string;
  batchSectionLabel: string;
  removedItem: string;
  emptyLine: string;
  badgeNew: string;
  badgeRemake: string;
  meatOpt: string;
  meatOptParen: string;
  remakeReason: string;
  orderNotesTitle: string;
  orderNotesBody: string;
  progressBig: string;
  progressItemsLabel: string;
  timer: string;
  typeBadge: string;
  progressBadge: string;
  readyLine: string;
  columnTitle: string;
  columnCount: string;
  columnEmpty: string;
  buffetCustomer: string;
  tableName: string;
  deliverySubtext: string;
  awaitingStatus: string;
  awaitingAmber: string;
  deltaHint: string;
  removedTag: string;
  buffetSubtext: string;
  readyForServiceLine: string;
  /** SLA urgency chip next to the ticket timer */
  slaChipTrack: string;
  slaChipMedium: string;
  slaChipCritical: string;
  slaChipUrgent: string;
  /** Order channel badge (color-coded); use with label text inside */
  typeBadgeDineIn: string;
  typeBadgeTakeaway: string;
  typeBadgeOnline: string;
  typeBadgeUber: string;
  typeBadgeDelivery: string;
  typeBadgeBuffet: string;
};

export function getKdsTypography(size: KitchenSLASettings['kitchenDisplayTextSize']): KdsTypography {
  const s = size ?? 'large';
  if (s === 'standard') {
    return {
      itemRow: 'text-lg',
      noteBody: 'text-sm font-semibold text-yellow-950',
      noteLabel: 'text-[10px] font-extrabold uppercase tracking-wide text-yellow-950',
      doneBtn: 'text-base font-semibold px-3 py-2 rounded transition-colors',
      startBtn: 'w-full text-base font-semibold px-4 py-2.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center gap-2',
      removeBtn: 'w-full mt-3 text-sm font-semibold px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center justify-center gap-1',
      cardTitle: 'font-bold text-base sm:text-lg md:text-xl leading-tight break-words',
      runningTitle: 'font-extrabold text-base sm:text-lg md:text-xl text-red-700 uppercase leading-tight break-words',
      runningSub: 'text-sm sm:text-base text-gray-800 font-semibold leading-tight break-words',
      batchTimeLine: 'text-sm text-gray-600 mt-0.5',
      batchSectionLabel: 'text-xs font-semibold text-gray-600 border-b border-gray-200 pb-1',
      removedItem: 'text-sm text-red-900 flex items-center gap-2 rounded-md border border-red-400/70 bg-red-100 px-2 py-1.5 font-semibold shadow-sm',
      emptyLine: 'text-sm text-gray-500 italic',
      badgeNew: 'inline-flex items-center rounded-md bg-emerald-600 px-2 py-0.5 text-xs font-black uppercase tracking-wide text-white ring-2 ring-emerald-200/85 shadow-sm',
      badgeRemake: 'text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-300',
      meatOpt: 'text-xs font-bold text-pink-800 bg-pink-100 px-2 py-0.5 rounded border border-pink-300',
      meatOptParen: 'text-xs text-pink-600',
      remakeReason: 'text-xs text-purple-600 mt-0.5',
      orderNotesTitle: 'text-xs font-extrabold text-yellow-950 mb-1',
      orderNotesBody: 'text-xs font-semibold text-yellow-900',
      progressBig: 'text-2xl sm:text-3xl font-extrabold leading-none text-blue-700',
      progressItemsLabel: 'text-[10px] sm:text-xs font-semibold text-blue-600',
      timer: 'text-xs sm:text-sm text-gray-500 flex items-center',
      typeBadge: 'inline-flex items-center rounded bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700',
      progressBadge: 'inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700',
      readyLine: 'text-xs text-green-600 font-semibold mt-0.5',
      columnTitle: 'text-lg sm:text-xl font-bold text-gray-800 flex items-center',
      columnCount: 'ml-2 bg-gray-300 text-gray-700 text-sm font-semibold rounded-full px-2.5 py-0.5',
      columnEmpty: 'text-base text-gray-500',
      buffetCustomer: 'text-xs text-pink-600 font-semibold',
      tableName: 'font-bold text-lg md:text-xl text-gray-900 uppercase',
      deliverySubtext: 'text-xs text-gray-500 truncate',
      awaitingStatus: 'inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-green-600',
      awaitingAmber: 'inline-flex items-center gap-1 text-[10px] sm:text-xs text-amber-600',
      deltaHint: 'ml-1 inline-flex items-center rounded-md border border-red-500/70 bg-red-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-red-900',
      removedTag: 'inline-flex items-center rounded-md border border-red-600/70 bg-red-600 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white',
      buffetSubtext: 'text-xs text-gray-500',
      readyForServiceLine: 'text-xs text-green-600 font-semibold mt-0.5',
      slaChipTrack:
        'inline-flex items-center text-[10px] sm:text-xs font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 border border-emerald-400',
      slaChipMedium:
        'inline-flex items-center text-[10px] sm:text-xs font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-amber-100 text-amber-950 border border-amber-400',
      slaChipCritical:
        'inline-flex items-center text-[10px] sm:text-xs font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-red-100 text-red-900 border border-red-500',
      slaChipUrgent:
        'inline-flex items-center text-[10px] sm:text-xs font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-red-200 text-red-950 border-2 border-red-700 ring-2 ring-red-400/80',
      typeBadgeDineIn:
        'inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-900 border border-slate-400',
      typeBadgeTakeaway:
        'inline-flex items-center rounded-md bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-900 border border-orange-400',
      typeBadgeOnline:
        'inline-flex items-center rounded-md bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-950 border border-sky-500',
      typeBadgeUber:
        'inline-flex items-center rounded-md bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-950 border border-violet-500',
      typeBadgeDelivery:
        'inline-flex items-center rounded-md bg-teal-100 px-2 py-0.5 text-xs font-bold text-teal-950 border border-teal-500',
      typeBadgeBuffet:
        'inline-flex items-center rounded-md bg-purple-100 px-2 py-0.5 text-xs font-bold text-purple-900 border border-purple-400',
    };
  }
  if (s === 'xlarge') {
    return {
      itemRow: 'text-3xl',
      noteBody: 'text-lg font-semibold text-yellow-950',
      noteLabel: 'text-sm font-extrabold uppercase tracking-wide text-yellow-950',
      doneBtn: 'text-xl font-semibold px-4 py-2.5 rounded transition-colors',
      startBtn: 'w-full text-xl font-semibold px-5 py-3 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center gap-2',
      removeBtn: 'w-full mt-3 text-lg font-semibold px-4 py-2.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center justify-center gap-1',
      cardTitle: 'font-bold text-xl sm:text-2xl md:text-3xl leading-tight break-words',
      runningTitle: 'font-extrabold text-xl sm:text-2xl md:text-3xl text-red-700 uppercase leading-tight break-words',
      runningSub: 'text-lg sm:text-xl text-gray-800 font-semibold leading-tight break-words',
      batchTimeLine: 'text-base text-gray-600 mt-0.5',
      batchSectionLabel: 'text-base font-semibold text-gray-600 border-b border-gray-200 pb-1',
      removedItem: 'text-lg text-red-900 flex items-center gap-2 rounded-md border border-red-400/70 bg-red-100 px-2.5 py-2 font-semibold shadow-sm',
      emptyLine: 'text-lg text-gray-500 italic',
      badgeNew: 'inline-flex items-center rounded-md bg-emerald-600 px-2.5 py-1 text-base font-black uppercase tracking-wide text-white ring-2 ring-emerald-200/85 shadow-sm',
      badgeRemake: 'text-base font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-300',
      meatOpt: 'text-base font-bold text-pink-800 bg-pink-100 px-2 py-0.5 rounded border border-pink-300',
      meatOptParen: 'text-base text-pink-600',
      remakeReason: 'text-base text-purple-600 mt-0.5',
      orderNotesTitle: 'text-base font-extrabold text-yellow-950 mb-1',
      orderNotesBody: 'text-base font-semibold text-yellow-900',
      progressBig: 'text-4xl sm:text-5xl font-extrabold leading-none text-blue-700',
      progressItemsLabel: 'text-sm sm:text-base font-semibold text-blue-600',
      timer: 'text-sm sm:text-base text-gray-500 flex items-center',
      typeBadge: 'inline-flex items-center rounded bg-slate-200 px-2 py-0.5 text-sm font-semibold text-slate-700',
      progressBadge: 'inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-sm font-semibold text-blue-700',
      readyLine: 'text-sm text-green-600 font-semibold mt-0.5',
      columnTitle: 'text-2xl sm:text-3xl font-bold text-gray-800 flex items-center',
      columnCount: 'ml-2 bg-gray-300 text-gray-700 text-base font-semibold rounded-full px-2.5 py-0.5',
      columnEmpty: 'text-lg text-gray-500',
      buffetCustomer: 'text-sm text-pink-600 font-semibold',
      tableName: 'font-bold text-2xl md:text-3xl text-gray-900 uppercase',
      deliverySubtext: 'text-sm text-gray-500 truncate',
      awaitingStatus: 'inline-flex items-center gap-1 text-sm sm:text-base font-semibold text-green-600',
      awaitingAmber: 'inline-flex items-center gap-1 text-sm sm:text-base text-amber-600',
      deltaHint: 'ml-1 inline-flex items-center rounded-md border border-red-500/70 bg-red-100 px-2 py-0.5 text-sm font-black uppercase tracking-wide text-red-900',
      removedTag: 'inline-flex items-center rounded-md border border-red-600/70 bg-red-600 px-2 py-0.5 text-sm font-black uppercase tracking-wide text-white',
      buffetSubtext: 'text-base text-gray-500',
      readyForServiceLine: 'text-base text-green-600 font-semibold mt-0.5',
      slaChipTrack:
        'inline-flex items-center text-sm sm:text-base font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-900 border border-emerald-400',
      slaChipMedium:
        'inline-flex items-center text-sm sm:text-base font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-md bg-amber-100 text-amber-950 border border-amber-400',
      slaChipCritical:
        'inline-flex items-center text-sm sm:text-base font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-md bg-red-100 text-red-900 border border-red-500',
      slaChipUrgent:
        'inline-flex items-center text-sm sm:text-base font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-md bg-red-200 text-red-950 border-2 border-red-700 ring-2 ring-red-400/80',
      typeBadgeDineIn:
        'inline-flex items-center rounded-md bg-slate-200 px-2.5 py-1 text-sm font-bold text-slate-900 border border-slate-400',
      typeBadgeTakeaway:
        'inline-flex items-center rounded-md bg-orange-100 px-2.5 py-1 text-sm font-bold text-orange-900 border border-orange-400',
      typeBadgeOnline:
        'inline-flex items-center rounded-md bg-sky-100 px-2.5 py-1 text-sm font-bold text-sky-950 border border-sky-500',
      typeBadgeUber:
        'inline-flex items-center rounded-md bg-violet-100 px-2.5 py-1 text-sm font-bold text-violet-950 border border-violet-500',
      typeBadgeDelivery:
        'inline-flex items-center rounded-md bg-teal-100 px-2.5 py-1 text-sm font-bold text-teal-950 border border-teal-500',
      typeBadgeBuffet:
        'inline-flex items-center rounded-md bg-purple-100 px-2.5 py-1 text-sm font-bold text-purple-900 border border-purple-400',
    };
  }
  // large — default; readable on wide KDS
  return {
    itemRow: 'text-2xl',
    noteBody: 'text-base font-semibold text-yellow-950',
    noteLabel: 'text-xs font-extrabold uppercase tracking-wide text-yellow-950',
    doneBtn: 'text-lg font-semibold px-3 py-2 rounded transition-colors',
    startBtn: 'w-full text-lg font-semibold px-4 py-2.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center gap-2',
    removeBtn: 'w-full mt-3 text-base font-semibold px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center justify-center gap-1',
    cardTitle: 'font-bold text-lg sm:text-xl md:text-2xl leading-tight break-words',
    runningTitle: 'font-extrabold text-lg sm:text-xl md:text-2xl text-red-700 uppercase leading-tight break-words',
    runningSub: 'text-base sm:text-lg text-gray-800 font-semibold leading-tight break-words',
    batchTimeLine: 'text-base text-gray-600 mt-0.5',
    batchSectionLabel: 'text-sm font-semibold text-gray-600 border-b border-gray-200 pb-1',
    removedItem: 'text-base text-red-900 flex items-center gap-2 rounded-md border border-red-400/70 bg-red-100 px-2 py-1.5 font-semibold shadow-sm',
    emptyLine: 'text-base text-gray-500 italic',
    badgeNew: 'inline-flex items-center rounded-md bg-emerald-600 px-2 py-0.5 text-sm font-black uppercase tracking-wide text-white ring-2 ring-emerald-200/85 shadow-sm',
    badgeRemake: 'text-sm font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-300',
    meatOpt: 'text-sm font-bold text-pink-800 bg-pink-100 px-2 py-0.5 rounded border border-pink-300',
    meatOptParen: 'text-sm text-pink-600',
    remakeReason: 'text-sm text-purple-600 mt-0.5',
    orderNotesTitle: 'text-sm font-extrabold text-yellow-950 mb-1',
    orderNotesBody: 'text-sm font-semibold text-yellow-900',
    progressBig: 'text-3xl sm:text-4xl font-extrabold leading-none text-blue-700',
    progressItemsLabel: 'text-xs sm:text-sm font-semibold text-blue-600',
    timer: 'text-sm sm:text-base text-gray-500 flex items-center',
    typeBadge: 'inline-flex items-center rounded bg-slate-200 px-2 py-0.5 text-sm font-semibold text-slate-700',
    progressBadge: 'inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-sm font-semibold text-blue-700',
    readyLine: 'text-sm text-green-600 font-semibold mt-0.5',
    columnTitle: 'text-xl sm:text-2xl font-bold text-gray-800 flex items-center',
    columnCount: 'ml-2 bg-gray-300 text-gray-700 text-base font-semibold rounded-full px-2.5 py-0.5',
    columnEmpty: 'text-lg text-gray-500',
    buffetCustomer: 'text-sm text-pink-600 font-semibold',
    tableName: 'font-bold text-xl md:text-2xl text-gray-900 uppercase',
    deliverySubtext: 'text-sm text-gray-500 truncate',
    awaitingStatus: 'inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-green-600',
    awaitingAmber: 'inline-flex items-center gap-1 text-xs sm:text-sm text-amber-600',
    deltaHint: 'ml-1 inline-flex items-center rounded-md border border-red-500/70 bg-red-100 px-1.5 py-0.5 text-xs font-black uppercase tracking-wide text-red-900',
    removedTag: 'inline-flex items-center rounded-md border border-red-600/70 bg-red-600 px-1.5 py-0.5 text-xs font-black uppercase tracking-wide text-white',
    buffetSubtext: 'text-sm text-gray-500',
    readyForServiceLine: 'text-sm text-green-600 font-semibold mt-0.5',
    slaChipTrack:
      'inline-flex items-center text-xs sm:text-sm font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 border border-emerald-400',
    slaChipMedium:
      'inline-flex items-center text-xs sm:text-sm font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-amber-100 text-amber-950 border border-amber-400',
    slaChipCritical:
      'inline-flex items-center text-xs sm:text-sm font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-red-100 text-red-900 border border-red-500',
    slaChipUrgent:
      'inline-flex items-center text-xs sm:text-sm font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md bg-red-200 text-red-950 border-2 border-red-700 ring-2 ring-red-400/80',
    typeBadgeDineIn:
      'inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-sm font-bold text-slate-900 border border-slate-400',
    typeBadgeTakeaway:
      'inline-flex items-center rounded-md bg-orange-100 px-2 py-0.5 text-sm font-bold text-orange-900 border border-orange-400',
    typeBadgeOnline:
      'inline-flex items-center rounded-md bg-sky-100 px-2 py-0.5 text-sm font-bold text-sky-950 border border-sky-500',
    typeBadgeUber:
      'inline-flex items-center rounded-md bg-violet-100 px-2 py-0.5 text-sm font-bold text-violet-950 border border-violet-500',
    typeBadgeDelivery:
      'inline-flex items-center rounded-md bg-teal-100 px-2 py-0.5 text-sm font-bold text-teal-950 border border-teal-500',
    typeBadgeBuffet:
      'inline-flex items-center rounded-md bg-purple-100 px-2 py-0.5 text-sm font-bold text-purple-900 border border-purple-400',
  };
}
